import { userDisplayName } from '@clockwise/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { BookOpen, ChevronDown, LogOut, MessageSquare, PanelLeft, Settings } from 'lucide-react';
import { useState } from 'react';
import { FeedbackSheet } from '@/components/custom/feedback-sheet';
import { BrandDots } from '@/components/layout/brand-dots';
import { ThemeSwitcher } from '@/components/theme-switcher';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { timesheetApprovalsApi } from '@/lib/api';
import { signOut, useSession } from '@/lib/auth-client';
import { useMe } from '@/lib/me';
import { hasNavAccess, navItems, SETUP_PERMISSIONS } from '@/lib/navigation';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

function initialsFor(name: string | null | undefined, email: string | undefined): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    return (first + last).toUpperCase() || '?';
  }
  return email?.[0]?.toUpperCase() ?? '?';
}

export function AppSidebar({ offsetTop = 0 }: { offsetTop?: number }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const { state, setOpen, toggleSidebar } = useSidebar();

  const user = session.data?.user;
  const sessionLoading = session.isPending;
  const { user: me, permissions } = useMe();
  // The domain `users` row owns the name (it's what the employee page edits);
  // the Better Auth user's `name` is set once at sign-in and never synced.
  // Fall back to it only while `me` is still loading.
  const displayName = me ? userDisplayName(me) : (user?.name ?? user?.email);
  // `hasNavAccess`, not `permissions.has(item.permission)` — /dashboards needs
  // two ids, and filtering on the primary alone would render a link that the
  // route then refuses.
  const visibleNav = navItems.filter((item) => hasNavAccess(item, permissions));

  // Pending-approvals badge — only fetched when the caller has
  // `timesheet.approve` (the same gate that surfaces the nav item).
  // 60s stale time is short enough to feel live after the user approves
  // a row, long enough to avoid hammering the API on each page change.
  // Mutations on the approvals page invalidate this key explicitly for
  // instant feedback.
  const approvalsCountQuery = useQuery({
    queryKey: queryKeys.timesheetApprovals.count(),
    queryFn: () => timesheetApprovalsApi.count(),
    enabled: permissions.has('timesheet.approve'),
    staleTime: 60_000,
  });
  const approvalsBadge = approvalsCountQuery.data?.data.total ?? 0;
  const setupVisible = SETUP_PERMISSIONS.some((p) => permissions.has(p));
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const isActive = (url: string): boolean => location.pathname.startsWith(url);

  const handleSidebarClick = (e: React.MouseEvent): void => {
    if (state !== 'collapsed') return;
    const target = e.target as HTMLElement;
    if (!target.isConnected) return;
    if (
      !target.closest(
        'button, a, [role="button"], [role="menuitem"], [role="menu"], input, [data-interactive]',
      )
    ) {
      setOpen(true);
    }
  };

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    // clear() drops cached data outright; invalidateQueries() would briefly
    // serve the prior user's payload while a refetch ran in the background.
    queryClient.clear();
    await navigate({ to: '/login' });
  };

  return (
    <>
      <Sidebar
        collapsible="icon"
        onClick={handleSidebarClick}
        // The shadcn Sidebar is `fixed inset-y-0 h-svh` — always pinned to the
        // true viewport top/bottom, with no awareness of the announcement/
        // impersonation banners stacked above it. Without this, a live banner
        // (z-50) paints over the sidebar's own top ~44px, which is exactly
        // where the "Clockwise" brand row lives. Shifting `top` down by the
        // same combined banner height AppHeader/AppShellBody already use, and
        // shrinking `height` to match, keeps the bottom edge where it was
        // instead of overflowing past the viewport.
        style={offsetTop ? { top: offsetTop, height: `calc(100svh - ${offsetTop}px)` } : undefined}
      >
        <SidebarHeader>
          <div
            className={cn(
              'flex h-10 items-center justify-between px-2',
              // Collapsed (icon) rail: the row has no room for dots + text
              // beside the toggle button, so it stacks them instead — the
              // dots stay visible (the brand mark this task asked to keep
              // showing) while "Clockwise" itself hides.
              'group-data-[collapsible=icon]:h-auto group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-2 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-2',
            )}
          >
            <div className="flex min-w-0 items-center gap-2">
              <BrandDots />
              <span className="truncate font-semibold text-lg text-primary group-data-[collapsible=icon]:hidden">
                Clockwise
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                toggleSidebar();
              }}
            >
              <PanelLeft />
              <span className="sr-only">Toggle sidebar</span>
            </Button>
          </div>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleNav.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.url)}
                      tooltip={item.title}
                      data-nav="item"
                    >
                      <Link to={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span className="flex-1">{item.title}</span>
                        {item.url === '/approvals' && approvalsBadge > 0 ? (
                          <span
                            className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-semibold text-white group-data-[collapsible=icon]:hidden"
                            role="status"
                            aria-label={`${approvalsBadge} pending approvals`}
                          >
                            {approvalsBadge > 99 ? '99+' : approvalsBadge}
                          </span>
                        ) : null}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            {/* Every signed-in user may send feedback — no permission. */}
            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip="Send feedback"
                data-nav="item"
                onClick={(e) => {
                  e.stopPropagation();
                  setFeedbackOpen(true);
                }}
              >
                <MessageSquare className="h-4 w-4" />
                <span>Send feedback</span>
              </SidebarMenuButton>
            </SidebarMenuItem>

            {setupVisible ? (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isActive('/setup')}
                  tooltip="Setup"
                  data-nav="item"
                >
                  <Link to="/setup">
                    <Settings className="h-4 w-4" />
                    <span>Setup</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ) : null}

            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    disabled={sessionLoading}
                    // `data-nav-hover="highlight"` — same lime hover as the
                    // main nav rows (globals.css), without inheriting their
                    // geometry: this trigger keeps its own rounded-pill shape
                    // rather than the nav rows' chip radius.
                    data-nav-hover="highlight"
                    // `bg-surface-subtle` — same table-header/tab-track tint as
                    // the rest of the app, so the account row reads as its own
                    // card instead of floating bare against the sidebar.
                    // `rounded-full` (the app's pill radius) rather than the
                    // cva default's 6-8px. `border-border` matches the taupe
                    // hairline every other card/pill in the app uses.
                    // `group/account` — lets the open/closed chevron below
                    // read THIS row's own `data-state` (Radix sets it here
                    // via `DropdownMenuTrigger asChild`), independent of the
                    // sidebar's own unnamed collapsed/expanded group.
                    className="group/account rounded-full border border-border bg-surface-subtle data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  >
                    {sessionLoading ? (
                      <>
                        <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                        <div className="grid flex-1 gap-1.5">
                          <Skeleton className="h-3 w-24" />
                          <Skeleton className="h-3 w-32" />
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Collapsed rail: the avatar stays the visible
                            icon (it's the one that identifies the account),
                            same as it was before the chevron briefly took
                            this slot. */}
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-primary text-xs text-primary-foreground">
                            {initialsFor(displayName, user?.email)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                          <span className="truncate font-semibold">{displayName}</span>
                          <span className="truncate text-xs text-muted-foreground">
                            {user?.email}
                          </span>
                        </div>
                        {/* One icon, rotated — not a swap between two — so
                            open/close reads as a smooth 180° turn instead of
                            an instant cut from one glyph to the other. */}
                        <ChevronDown className="ml-auto transition-transform duration-200 group-data-[collapsible=icon]:hidden group-data-[state=open]/account:rotate-180" />
                      </>
                    )}
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                  side="bottom"
                  align="end"
                  sideOffset={4}
                >
                  <div className="flex items-center justify-between px-2 py-1.5">
                    <span className="text-xs text-muted-foreground">Theme</span>
                    <ThemeSwitcher />
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild className="gap-2">
                    <Link to="/guides-and-updates">
                      <BookOpen />
                      <span>Guides and Updates</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="gap-2">
                    <LogOut />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <FeedbackSheet open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </>
  );
}
